import { handleAuthorization, handleOkResponseJson, handleError, handlePreConfirmResponseJson } from "./handlers"
import { criptografarMD5 } from "./cryptographyService"

export default {
    loginAsync,
    changeAsync,
    sendEmailChange,
    changeByTokenAsync,
    validarTokenAsync,
    logout,
    obterToken,
    getUserRoles,
    getUserPermission,
    isLoggedUserAdmin,
    getModuleRule,
    canApproveModule,
    canDeclineModule,
    canInactivateModule,
    logApprovalDiagnostics,
    updateRoles
}

const parseStoredUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
}

const normalizeRole = (role) => {
    if (!role) return null;

    return {
        ...role,
        id: role.id ?? role.Id,
        idPermissao: role.idPermissao ?? role.IdPermissao,
        idModulo: role.idModulo ?? role.IdModulo ?? role.moduloId ?? role.ModuloId,
        moduloId: role.moduloId ?? role.ModuloId,
        modulo: role.modulo ?? role.Modulo,
        leitura: role.leitura ?? role.Leitura ?? false,
        escrita: role.escrita ?? role.Escrita ?? false,
        aprovar: role.aprovar ?? role.Aprovar ?? false,
        declinar: role.declinar ?? role.Declinar ?? false,
        inativar: role.inativar ?? role.Inativar ?? false,
        baixar: role.baixar ?? role.Baixar ?? false
    };
}

const isApprovalDebugEnabled = () => {
    return localStorage.getItem('debugAprovacaoBotoes') === 'true';
}

export const obterToken = () => {
    return parseStoredUser().token;
}

export const getUserName = () => {
    return parseStoredUser().nome;
}

export const getUserMail = () => {
    return parseStoredUser().email;
}

export const getUserPerfil = () => {
    const user = parseStoredUser();
    return user.perfil ?? user.Perfil;
}

export const getUserAvatar = () => {
    const user = parseStoredUser();
    return user.avatar ?? user.Avatar;
}

export const getUserRoles = () => {
    const user = parseStoredUser();
    return ((user && (user.roles || user.Roles)) || []).map(normalizeRole);
}

export const isLoggedUserAdmin = () => {
    const user = parseStoredUser();
    return (user && (user.admin ?? user.Admin)) === true;
}

export const getUserDepartments = () => {
    const user = parseStoredUser();

    const ids = ((user && (user.departamentos || user.Departamentos)) || [])
        .map((x) => x.idDepartamento ?? x.IdDepartamento);
    return ids;
}
export const getUserBuyerDepartments = () => {
    const user = parseStoredUser();

    const ids = ((user && (user.departamentos || user.Departamentos)) || [])
        .filter(item => (item.comprador ?? item.Comprador) === true)
        .map(item => item.idDepartamento ?? item.IdDepartamento);
    
    return ids;
}

export const getModuleRule = (modulo) => {
    return getUserRoles().find((r) => r.idModulo == modulo) || null;
}

export const hasModulePermission = (modulo) => {
    const regra = getModuleRule(modulo);
    return regra != null && regra.escrita === true;
}

export const canApproveModule = (modulo) => {
    const regra = getModuleRule(modulo);
    return regra != null && regra.escrita === true && regra.aprovar === true;
}

export const canDeclineModule = (modulo) => {
    if (isLoggedUserAdmin()) return true;

    const regra = getModuleRule(modulo);
    return regra != null && regra.escrita === true && regra.declinar === true;
}

export const canInactivateModule = (modulo) => {
    if (isLoggedUserAdmin()) return true;

    const regra = getModuleRule(modulo);
    return regra != null && regra.escrita === true && regra.inativar === true;
}

export const getUserPermission = (modulo) => {
    return hasModulePermission(modulo);
}

export const explainApprovalButtonState = ({
    modulo,
    registro,
    action = 'aprovar',
    statusPermitidos = [],
    departamentoObrigatorio = false,
    departamentosUsuario = [],
    mesmoUsuarioObrigatorio = false,
    mesmoUsuario = true,
    compradorObrigatorio = false,
    mesmoComprador = true
}) => {
    const regra = getModuleRule(modulo);
    const status = registro && registro.status;
    const reasons = [];
    const admin = isLoggedUserAdmin();

    if (admin && ['declinar', 'inativar'].includes(action)) {
        return {
            disabled: false,
            reasons,
            modulo,
            action,
            status,
            regra,
            admin,
            registro: registro
                ? {
                    id: registro.id,
                    status: registro.status,
                    idDepartamento: registro.idDepartamento,
                    mesmoSolicitante: registro.mesmoSolicitante,
                    mesmoRequisitante: registro.mesmoRequisitante,
                    mesmoComprador: registro.mesmoComprador
                }
                : null
        };
    }

    if (!regra) reasons.push('permissao_modulo_nao_carregada');
    if (regra && !regra.escrita) reasons.push('sem_escrita_no_modulo');
    if (action === 'aprovar' && regra && !regra.aprovar) reasons.push('sem_flag_aprovar');
    if (action === 'declinar' && regra && !regra.declinar) reasons.push('sem_flag_declinar');
    if (action === 'inativar' && regra && !regra.inativar) reasons.push('sem_flag_inativar');
    if (statusPermitidos.length > 0 && !statusPermitidos.includes(status)) reasons.push(`status_${status || 'vazio'}_nao_permitido`);
    if (departamentoObrigatorio && !departamentosUsuario.includes(registro && registro.idDepartamento)) reasons.push('departamento_nao_permitido');
    if (mesmoUsuarioObrigatorio && !mesmoUsuario) reasons.push('usuario_criador_diferente');
    if (compradorObrigatorio && !mesmoComprador) reasons.push('comprador_diferente');

    return {
        disabled: reasons.length > 0,
        reasons,
        modulo,
        action,
        status,
        regra,
        registro: registro
            ? {
                id: registro.id,
                status: registro.status,
                idDepartamento: registro.idDepartamento,
                mesmoSolicitante: registro.mesmoSolicitante,
                mesmoRequisitante: registro.mesmoRequisitante,
                mesmoComprador: registro.mesmoComprador
            }
            : null,
        admin
    };
}

export const logApprovalDiagnostics = (contexto, detalhes) => {
    if (!isApprovalDebugEnabled()) return;

    const user = parseStoredUser();
    /* eslint-disable no-console */
    console.group(`[aprovacao] ${contexto}`);
    console.log('usuario', {
        nome: user && (user.nome ?? user.Nome),
        perfil: user && (user.perfil ?? user.Perfil),
        admin: user && (user.admin ?? user.Admin)
    });
    console.log('permissoesCarregadas', getUserRoles());
    console.log('diagnostico', detalhes);
    console.groupEnd();
    /* eslint-enable no-console */
}

export const loginAsync = async (user, senha) => {
    var userName = criptografarMD5(user);
    var password = criptografarMD5(senha);
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName, password })
    };
    return fetch(`${process.env.VUE_APP_API_URL}/login`, requestOptions)
        .then(handleAuthorization)
        .then(handleOkResponseJson)
        .then(user => {
            if (user.data.token) {
                localStorage.setItem('user', JSON.stringify(user.data));
            }
            return user.data;
        });
}

export const changeAsync = async (senha) => {
    var password = criptografarMD5(senha);
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'bearer ' + obterToken(), password }
    };
    return fetch(`${process.env.VUE_APP_API_URL}/Login/alterarPrimeiroAcesso/`, requestOptions)
        .then(handleAuthorization)
        .then(handleError)
        .then(function () {
            var user = localStorage.getItem('user');
            user = JSON.parse(user);
            user.first = false;
            localStorage.setItem('user', JSON.stringify(user));
        });
}


export const updateRoles = async () => {
    const requestOptions = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'bearer ' + obterToken() }
    };
    return fetch(`${process.env.VUE_APP_API_URL}/Login/recarregarAcessos/`, requestOptions)
        .then(handleAuthorization)
        .then(handleOkResponseJson)
        .then(user => {
            if (user.data.token) {
                localStorage.setItem('user', JSON.stringify(user.data));
            }
            return user.data;
        });
}

export const sendEmailChange = async (email) => {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', email }
    };
    return fetch(`${process.env.VUE_APP_API_URL}/Login/getTokenResetSenha`, requestOptions)
        .then(handlePreConfirmResponseJson);
}

export const changeByTokenAsync = async (senha, token) => {
    var password = criptografarMD5(senha);
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'bearer ' + token, password }
    };
    return fetch(`${process.env.VUE_APP_API_URL}/Login/resetSenhaToken/`, requestOptions)
        .then(handlePreConfirmResponseJson);
}
export const validarTokenAsync = async (token) => {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'bearer ' + token }
    };
    return fetch(`${process.env.VUE_APP_API_URL}/Login/validarTokenResetSenha`, requestOptions)
        .then(handlePreConfirmResponseJson);
}

export const logout = () => {
    localStorage.removeItem('user');
}
